import { Flex, Container, Text, Icon } from "@chakra-ui/react";
import { NavLink, useLocation } from "react-router-dom";
import { Home, Megaphone, Info, Wind } from "lucide-react";
import styled, { useTheme } from "styled-components";
import { motion } from "framer-motion";
import { AppTheme } from "../theme";

const MotionFlex = motion(Flex);

const NavContainer = styled.nav<{ $theme: AppTheme }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${({ $theme }) => $theme.navBackground};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-top: 1px solid ${({ $theme }) => $theme.navBorder};
  box-shadow: ${({ $theme }) => $theme.navShadow};
  padding-bottom: env(safe-area-inset-bottom, 0);
`;

const NavItem = styled(NavLink)<{ $theme: AppTheme }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.25rem;
  color: ${({ $theme }) => $theme.navInactive};
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  flex: 1;
  max-width: 140px;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%) scaleX(0);
    width: 50px;
    height: 3px;
    background: ${({ $theme }) => $theme.gradientPrimary};
    border-radius: 0 0 6px 6px;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &.active {
    color: ${({ $theme }) => $theme.navActive};

    &::before {
      transform: translateX(-50%) scaleX(1);
    }
  }

  &:hover:not(.active) {
    color: ${({ $theme }) => $theme.text};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const NavLabel = styled(Text)`
  font-family: 'Outfit', sans-serif;
  font-size: 0.65rem;
  font-weight: 600;
  margin-top: 0.35rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const IconWrapper = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NotificationDot = styled(motion.div)<{ $theme: AppTheme }>`
  position: absolute;
  top: -3px;
  right: -3px;
  width: 10px;
  height: 10px;
  background: ${({ $theme }) => $theme.accent};
  border-radius: 50%;
  border: 2px solid ${({ $theme }) => $theme.mode === 'light' ? 'white' : $theme.background2};
  box-shadow: 0 2px 8px ${({ $theme }) => $theme.accent}40;
`;

export function Navbar() {
  const location = useLocation();
  const theme = useTheme() as AppTheme;
  const isHomeActive = location.pathname === "/";
  const isNewsActive = location.pathname === "/aktualnosci";
  const isAirQualityActive = location.pathname === "/jakosc-powietrza";
  const isAboutActive = location.pathname === "/o-aplikacji";

  return (
    <NavContainer $theme={theme}>
      <Container maxW="container.md" px={0}>
        <Flex justify="center" align="center">
          <NavItem 
            to="/" 
            $theme={theme}
            className={isHomeActive ? "active" : ""}
          >
            <IconWrapper
              animate={{ 
                scale: isHomeActive ? 1.1 : 1,
                rotate: isHomeActive ? [0, -5, 5, 0] : 0 
              }}
              transition={{ duration: 0.3 }}
            >
              <Icon 
                as={Home} 
                boxSize={5}
                strokeWidth={isHomeActive ? 2.5 : 2}
              />
            </IconWrapper>
            <NavLabel>Harmonogram</NavLabel>
          </NavItem>

          <NavItem
            to="/jakosc-powietrza"
            $theme={theme}
            className={isAirQualityActive ? "active" : ""}
          >
            <IconWrapper
              animate={{ 
                scale: isAirQualityActive ? 1.1 : 1,
              }}
              transition={{ duration: 0.3 }}
            >
              <Icon 
                as={Wind} 
                boxSize={5}
                strokeWidth={isAirQualityActive ? 2.5 : 2}
              />
            </IconWrapper>
            <NavLabel>Powietrze</NavLabel>
          </NavItem>

          <NavItem
            to="/aktualnosci"
            $theme={theme}
            className={isNewsActive ? "active" : ""}
          >
            <MotionFlex 
              position="relative"
              animate={{ 
                scale: isNewsActive ? 1.1 : 1,
              }}
              transition={{ duration: 0.3 }}
            >
              <Icon 
                as={Megaphone} 
                boxSize={5}
                strokeWidth={isNewsActive ? 2.5 : 2}
              />
              <NotificationDot 
                $theme={theme}
                animate={{ 
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </MotionFlex>
            <NavLabel>Aktualności</NavLabel>
          </NavItem>

          <NavItem
            to="/o-aplikacji"
            $theme={theme}
            className={isAboutActive ? "active" : ""}
          >
            <IconWrapper
              animate={{ 
                scale: isAboutActive ? 1.1 : 1,
              }}
              transition={{ duration: 0.3 }}
            >
              <Icon 
                as={Info} 
                boxSize={5}
                strokeWidth={isAboutActive ? 2.5 : 2}
              />
            </IconWrapper>
            <NavLabel>O aplikacji</NavLabel>
          </NavItem>
        </Flex>
      </Container>
    </NavContainer>
  );
}
